import React, { useEffect, useRef, useState } from "react";
import { HomeOutlined, SearchOutlined } from "@ant-design/icons";
import { Breadcrumb, Button, Input, notification, Modal, Space, Table } from "antd";
import { useNavigate } from "react-router-dom";
import type { InputRef, TableColumnsType, TableColumnType } from 'antd';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import "./data_goods_receive_return.css";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";

interface DataType {
    key: string;
    goodsReceiveReturnNo: string;
    itemGroup: string;
    date: string;
    grpoDocNo: string;
    supplierName: string;
    receivedQty: number;
    totalAmount: number;
    status: string;
}

type DataIndex = keyof DataType;

const DataGoodsReceiveReturn: React.FC = () => {
    const { userData } = useAuth();
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };

    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);
    const navigate = useNavigate();
    const [data, setData] = useState<DataType[]>([]);
    const [loading, setLoading] = useState(true);

    const itemGroupMapping: { [key: string]: string } = {
        '1': 'Inventory',
        '2': 'Outside Job'
    }

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/all-goods-receive-return`
            );
            setData(response.data);
        } catch (error) {
            console.error("Failed to fetch goods receive return data:", error);
            notification.error({ message: "Gagal memuat data.", duration: 4 });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleStatusUpdate = async (goodsReceiveReturnNo: string, status: string, successMessage: string) => {
        try {
            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/goods-receive-return/update-status`, {
                goods_receive_return_no: goodsReceiveReturnNo,
                status: status,
            });
            notification.success({ message: successMessage, duration: 4 });
            fetchData();
        } catch (error) {
            console.error("Failed to update status:", error);
            notification.error({ message: "Gagal memperbarui status.", duration: 4 });
        }
    };

    const showCancelConfirm = (record: DataType) => {
        Modal.confirm({
            title: 'Konfirmasi Pembatalan atau Koreksi',
            content: `Apakah permintaan return pada nomor "${record.goodsReceiveReturnNo}" akan di batalkan atau di kembalikan untuk koreksi?`,
            okText: 'Correct',
            okButtonProps: { style: { backgroundColor: '#1890ff', borderColor: '#1890ff' } },
            cancelText: 'Batal',
            cancelButtonProps: { danger: true },
            onOk() {
                handleStatusUpdate(record.goodsReceiveReturnNo, '1', `Dokumen ${record.goodsReceiveReturnNo} dikembalikan untuk koreksi.`);
            },
            onCancel() {
                handleStatusUpdate(record.goodsReceiveReturnNo, '5', `Dokumen ${record.goodsReceiveReturnNo} telah dibatalkan.`);
            },
        });
    };

    const handleRowDoubleClick = (record: DataType) => {
        navigate('/dashboard/goods_receive_return', { state: { goodsReceiveReturnNo: record.goodsReceiveReturnNo } });
    };

    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<DataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) =>
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        filterDropdownProps: {
            onOpenChange(open) {
                if (open) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    const baseColumns: TableColumnsType<DataType> = [
        {
            title: 'Goods Receive Return No',
            dataIndex: 'goodsReceiveReturnNo',
            key: 'goodsReceiveReturnNo',
            ...getColumnSearchProps('goodsReceiveReturnNo'),
            sorter: (a, b) => a.goodsReceiveReturnNo.localeCompare(b.goodsReceiveReturnNo),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Item Group',
            dataIndex: 'itemGroup',
            key: 'itemGroup',
            ...getColumnSearchProps('itemGroup'),
            sorter: (a, b) => a.itemGroup.localeCompare(b.itemGroup),
            sortDirections: ['descend', 'ascend'],
            render: (itemGroup: string) => itemGroupMapping[itemGroup] || itemGroup,
        },
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            ...getColumnSearchProps('date'),
            sorter: (a, b) => a.date.localeCompare(b.date),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Grpo Doc No',
            dataIndex: 'grpoDocNo',
            key: 'grpoDocNo',
            ...getColumnSearchProps('grpoDocNo'),
            sorter: (a, b) => a.grpoDocNo.localeCompare(b.grpoDocNo),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Supplier Name',
            dataIndex: 'supplierName',
            key: 'supplierName',
            ...getColumnSearchProps('supplierName'),
            sorter: (a, b) => a.supplierName.localeCompare(b.supplierName),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Received Qty',
            dataIndex: 'receivedQty',
            key: 'receivedQty',
            ...getColumnSearchProps('receivedQty'),
            sorter: (a, b) => a.receivedQty - b.receivedQty,
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Total Amount',
            dataIndex: 'totalAmount',
            key: 'totalAmount',
            ...getColumnSearchProps('totalAmount'),
            sorter: (a, b) => a.totalAmount - b.totalAmount,
            sortDirections: ['descend', 'ascend'],
            render: (text: number) => new Intl.NumberFormat("id-ID").format(text),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            ...getColumnSearchProps('status'),
            sorter: (a, b) => a.status.localeCompare(b.status),
            sortDirections: ['descend', 'ascend'],
        },
    ];

    const columns = [...baseColumns];
    return (
        <div id="ieml" style={transformStyle}>
            <div className="gjs-grid-row" id="i5af">
                <div className="gjs-grid-column" id="il3y">
                    <Breadcrumb
                        items={[
                            {
                                title: <HomeOutlined />,
                            },
                            {
                                title: (
                                    <>
                                        <span>Data Goods Receive Return</span>
                                    </>
                                ),
                            },
                            {
                                href: "#/dashboard/goods_receive_return",
                                title: "Goods Receive Retur - SP",
                            },
                        ]}
                    />
                </div>
            </div>
            <div className="gjs-grid-row" id="ifb8m">
                <div className="gjs-grid-column" id="ikwxk">
                    <Table<DataType>
                        columns={columns}
                        dataSource={data}
                        loading={loading}
                        onRow={(record) => {
                            return {
                                onDoubleClick: () => handleRowDoubleClick(record),
                            };
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default DataGoodsReceiveReturn;